import * as THREE from 'three';

export class Starfield {
  constructor(scene) {
    this.scene = scene;
    this.stars = null;
    this.starSpeeds = null;
    this.scrollOffset = 0;
    this.lastScrollY = 0;
    this.cubeVisible = true; // Track if cube is visible for collision detection
    this.init();
  }

  init() {
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 1000;
    const starPositions = new Float32Array(starCount * 3);
    this.starSpeeds = new Float32Array(starCount);

    for (let i = 0; i < starCount; i++) {
      starPositions[i * 3] = (Math.random() - 0.5) * 50;
      starPositions[i * 3 + 1] = (Math.random() - 0.5) * 50;
      starPositions[i * 3 + 2] = (Math.random() - 0.5) * 50;
      this.starSpeeds[i] = Math.random() * 0.01 + 0.005;
    }

    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));

    // Create circular star texture
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.5, 'rgba(255,255,255,0.5)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 32, 32);
    const starTexture = new THREE.CanvasTexture(canvas);

    this.starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.15,
      transparent: true,
      opacity: 0.8,
      fog: false,
      map: starTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    this.stars = new THREE.Points(starGeometry, this.starMaterial);
    this.scene.add(this.stars);
  }

  setStarColor(color) {
    if (this.starMaterial) {
      this.starMaterial.color.setHex(color);
    }
  }

  update(scrollY = 0) {
    if (!this.stars) return;

    // Calculate scroll delta for parallax effect
    const scrollDelta = (scrollY - this.lastScrollY) * 0.01;
    this.lastScrollY = scrollY;
    this.scrollOffset += scrollDelta;

    const positions = this.stars.geometry.attributes.position.array;

    // Cube bounds (approximate cube size: 5x5x5 centered at origin)
    const cubeRadius = 3; // Safe zone around cube

    for (let i = 0; i < positions.length / 3; i++) {
      // Move stars towards camera (negative Z) - base animation
      positions[i * 3 + 2] += this.starSpeeds[i];

      // Add scroll-based parallax movement
      // Different layers move at different speeds based on their Z position
      const depth = positions[i * 3 + 2];
      const parallaxFactor = (depth + 25) / 30; // Stars further away move slower

      // Move stars vertically with scroll (parallax effect)
      positions[i * 3 + 1] -= scrollDelta * parallaxFactor * 2;

      // Slight horizontal drift based on scroll for more dynamic feel
      positions[i * 3] += scrollDelta * parallaxFactor * 0.5 * (Math.sin(i * 0.1) - 0.5);

      // Check if star is in cube collision zone (only if cube is visible)
      if (this.cubeVisible) {
        const x = positions[i * 3];
        const y = positions[i * 3 + 1];
        const z = positions[i * 3 + 2];

        const isInCubeZone = Math.abs(x) < cubeRadius &&
                             Math.abs(y) < cubeRadius &&
                             z > -cubeRadius && z < cubeRadius;

        if (isInCubeZone) {
          // Star is colliding with cube - respawn it far away
          positions[i * 3] = (Math.random() - 0.5) * 50;
          positions[i * 3 + 1] = (Math.random() - 0.5) * 50;
          positions[i * 3 + 2] = -25;
        }
      }

      // Reset star position if it gets too close
      if (positions[i * 3 + 2] > 5) {
        positions[i * 3] = (Math.random() - 0.5) * 50;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 50;
        positions[i * 3 + 2] = -25;
      }

      // Wrap stars vertically to create infinite scrolling effect
      if (positions[i * 3 + 1] > 25) {
        positions[i * 3 + 1] = -25;
      } else if (positions[i * 3 + 1] < -25) {
        positions[i * 3 + 1] = 25;
      }

      // Wrap stars horizontally
      if (positions[i * 3] > 25) {
        positions[i * 3] = -25;
      } else if (positions[i * 3] < -25) {
        positions[i * 3] = 25;
      }
    }

    this.stars.geometry.attributes.position.needsUpdate = true;
  }
}
